import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { searchCommunicationTemplates } from '../../../../store/communicationService';
import {
    loadParticipantStatusDetails,
    loadParticipantStatusReasonDetails
} from '../../../../store/directusService';
import { loadDocmanTriggers } from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import {
    COMMUNICATION_TEMPLATES_ERROR,
    DOCMAN_TRIGGER_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreateDocmanTrigger from './CreateDocmanTrigger';
import DocmanTriggerRow from './DocmanTriggerRow';

import app from '../../../../app.module.css';

const headCells = [
    { id: 'snomedCode', numeric: false, label: 'SNOMED Code' },
    { id: 'status', numeric: false, label: 'Trigger Status' },
    { id: 'statusReasons', numeric: false, label: 'Status Reason Triggers' },
    { id: 'emailTemplate', numeric: false, label: 'Email Template' },
    { id: 'active', numeric: false, label: 'Active' },
    { id: 'snomedId', numeric: false, label: 'SNOMED ID' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'status',
    page: 0,
    rowsPerPage: 100
};

const DocmanTriggerAdmin = ({ contractId }) => {
    const dispatch = useDispatch();
    const docmanRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [selectedRow, setSelectedRow] = useState({});
    const [formType, setFormType] = useState('');

    // STORE STATE
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const participantStatusReasonDetails = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const communicationTemplates = useSelector(
        (state) => state.entities.communicationService.communicationTemplates
    );
    const docmanTriggers = useSelector((state) => state.entities.participantService.docmanTriggers);
    const loadingDocmanTriggers = useSelector(
        (state) => state.entities.participantService.loadingDocmanTriggers
    );

    // USE EFFECTS
    useEffect(() => {
        participantStatusDetails?.length < 1 && dispatch(loadParticipantStatusDetails());
        participantStatusReasonDetails?.length < 1 &&
            dispatch(loadParticipantStatusReasonDetails());
    }, []);

    useEffect(() => {
        dispatch(loadDocmanTriggers(contractId));
        dispatch(
            searchCommunicationTemplates({
                type: 'Email',
                contractIds: [contractId]
            })
        );
    }, [contractId]);

    useEffect(() => {
        if (!communicationTemplates?.length) return;
        if (!docmanTriggers?.length) rows.length && setRows([]);
        if (docmanTriggers?.length)
            setRows(
                docmanTriggers.map((el) => ({
                    ...el,
                    emailTemplate:
                        communicationTemplates.find((entry) => entry.id === el.emailTemplateId)
                            ?.name || '-'
                }))
            );
    }, [docmanTriggers, communicationTemplates]);

    // HELPER FNS
    const loadingError = () => {
        if (!communicationTemplates?.length) return COMMUNICATION_TEMPLATES_ERROR;
    };

    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <DocmanTriggerRow
                key={el.id}
                row={el}
                onEdit={onEdit}
                isEditDisabled={formType !== ''}
            />
        ));
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setFormType('edit');
        setSelectedRow(row);
    };

    const onClose = () => {
        setFormType('');
        setSelectedRow({});
        scrollToFocus(docmanRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div ref={docmanRef}>
            <h2 className={app.sectionHeading}>Triggers</h2>
            {rows.length < 1 || loadingDocmanTriggers ? (
                <LoadingSpinner content={DOCMAN_TRIGGER_ERROR} />
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}

            <div>
                {!formType.length ? (
                    <Button
                        id="addTriggerButton"
                        type="button"
                        content="ADD NEW TRIGGER"
                        onClick={onCreate}
                    />
                ) : (
                    <CreateDocmanTrigger
                        onClose={onClose}
                        formType={formType}
                        trigger={selectedRow}
                        contractId={contractId}
                    />
                )}
            </div>
        </div>
    );
};

DocmanTriggerAdmin.propTypes = {
    contractId: PropTypes.string
};

export default DocmanTriggerAdmin;
